import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { isArray, isNil, isObject, sumBy } from 'lodash';
import DOMPurify from 'dompurify';

import Store from 'state/Store';
import { formatDollarAmount, formatPercentAmount } from 'services/Formats';
import { isRowGroup } from 'components/layouts/Account/ColumnDefs';
import { getMostRecentDate, mmddyyyy } from 'services/Dates';
import { getAdviceGridAccounts } from 'state/account/AccountsGridSelectors';
import {
  ACTIVE_ADVISORY,
  isActiveAdvisory,
  isBrokerage,
  isActivePlus,
  isWorkplaceRetirement,
  products,
  ALASKA_529_CLASS,
  MARYLAND_529_CLASS,
  NATIONAL_529_CLASS,
  COLLEGE_SAVINGS_CLASS,
  isAdviceProductClass,
  isViewOnlyAccount,
  isPershingProduct,
  isBrokerageOrActiveAdvisory
} from 'services/Products';
import { MoreButton } from 'components/shared/Buttons/GridCellButtons';
import { ReactComponent as ExportToCSV } from 'assets/icons/exportToCSV.svg';
import numeral from 'numeral';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import BrokerageLink from 'components/shared/Table/BrokerageLink';
import { activePlusAccountUrl } from 'services/Externals';
import Content from 'services/Content';
import { adjustPercent, percentChange } from 'services/Calculation';
import { getIntradayRefreshEnabled } from 'state/account/AccountSelectors';
import {
  getUberId,
  getCustomerWebView
} from 'state/customer/CustomerSelectors';
import ValueIndicator from 'components/shared/ValueIndicator/ValueIndicator';
import { ReactComponent as Chevron } from 'assets/icons/chevron-down-icon.svg';
import BenchmarkModal from 'components/shared/Modal/BenchmarkModal';
import {
  appBenchmarkTooltipStringToNumber,
  rasBenchmarkStringToNumber
} from 'components/shared/Modal/BenchmarkModalContent';
import { openBrokerageAuthTab } from './ExternalAuthorizationURLBuilder';
import { GLOBALS } from 'services/globals';
import IntradayRefresh from 'components/shared/Intraday/IntradayRefresh';
import { onPerformancePage } from 'state/view/ViewSelectors';
import IntradayAsOfDate from '../components/shared/Intraday/IntradayAsOfDate';
import AccountLabel from 'components/shared/AccountLabel/AccountLabel';
import {
  shareClassConversionEnabled
} from './FeatureFlags';
import { intradayEligibleFilter } from './Accounts';

// Double-space to ensure intentional blanks.
export const BLANK_CELL_PLACEHOLDER = '  ';
export const EMPTY_CELL_DASH = '-';
export const CASH_ROW_NAME = 'Cash';

// shared functions

export const calculateRowHeight = (
  data,
  api,
  isDesktopLayout = true,
  sweepMap = {}
) => {
  // for row heights, since autoHeight only seems to work after some time.
  // height is multiple of 24; calculates how many lines there are in the account name given a line is 20 chars.
  if (!data) {
    return api.getSizesForCurrentTheme.rowHeight;
  }

  const rowHeightMultiple = api.getSizesForCurrentTheme().rowHeight / 3;
  const lineLength = isDesktopLayout ? 18.5 : 20;

  if (shareClassConversionEnabled()) {
    // keys explain the reasoning behind each addition in height

    const {
      accountNameAndNumber,
      mmrMessage,
      isViewOnly,
      sweepAccount,
      activeShareClassConverted,
      balanceAsOfDate
    } = data;

    const rowHeightMap = {
      longerLine: accountNameAndNumber ? data.accountNameAndNumber.length : 0,
      buttonHeight: isDesktopLayout ? 0 : 32,
      mmrMessage: mmrMessage ? 260 : 0,
      mrrMessageMobile: isDesktopLayout ? 0 : 90,
      viewOnlyLabel: isViewOnly ? 25 : 0,
      sweepLabel: sweepAccount ? 25 : 0,
      shareClassLabel: activeShareClassConverted ? 25 : 0,
      asOfLabel:
        isBrokerageOrActiveAdvisory(data.productType) &&
        balanceAsOfDate < getMostRecentDate(1)
          ? 25
          : 0
    };

    return (
      rowHeightMultiple *
      (Math.round(sumBy(Object.values(rowHeightMap)) / lineLength) + 1)
    );
  }

  const longerLine = data ? data.accountNameAndNumber.length : 0;
  const sweepLabel = sweepMap[data.accountId] !== undefined ? 25 : 0;
  const viewOnlyLabel = data && isViewOnlyAccount(data) ? 25 : 0;
  const asOfLabel =
    data &&
    (isBrokerage(data.productType) || isActiveAdvisory(data.productType)) &&
    data.balanceAsOfDate < getMostRecentDate(1)
      ? 25
      : 0;
  const mmrMessage = data && data.mmrMessage ? 260 : 0;
  const buttonHeight = isDesktopLayout ? 0 : 32;
  const mrrMessageMobile = isDesktopLayout ? 0 : 90;
  const rowHeight =
    rowHeightMultiple *
    (Math.round(
      (longerLine +
        buttonHeight +
        sweepLabel +
        viewOnlyLabel +
        asOfLabel +
        mmrMessage +
        mrrMessageMobile) /
        lineLength
    ) +
      1);

  return rowHeight;
};

export const MAIN_HEADER_CLASS = 'pi-row';
export const SUB_HEADER_CLASS = 'product-row';
export const BENCHMARK_CLASS = 'model-benchmark-row';

export const getRowClassRules = (group, field) => {
  if (group) {
    if (
      [
        'personalInvestment',
        'workplaceRetirement',
        'productClass',
        'tracRetailRetirement'
      ].includes(field)
    ) {
      return MAIN_HEADER_CLASS;
    } else if (
      field === 'productType' ||
      field === 'isOwner' ||
      field === 'retirementAccount'
    ) {
      return SUB_HEADER_CLASS;
    } else if (field === 'primaryOwner' || field === 'typeAndOwners') {
      return 'owner-row';
    } else if (field === 'rasBenchmarkRow') {
      return BENCHMARK_CLASS;
    }
  }
};

// returns column defs depending on filter combinations
export const chooseDefs = (
  columnDefs = [],
  columnDefsRetirementOrder = [],
  managementFilterType,
  accountFilterType,
  sortFilterType = '',
  is529Only = false,
  shouldUseAggregatedPerformance = false
) => {
  // just for visualizing what's happening to the array: only includes all array items not equal to removee
  const remove = (removee, arr) => arr.filter((curr) => curr !== removee);

  // default grid groupings: Personal Investments, Retirement
  let ignore = ['isOwner', 'productType'];

  // remove PI grouping, introduce productType grouping
  if (accountFilterType && accountFilterType.toLowerCase() !== 'all') {
    ignore.push('personalInvestment');
  }

  if (shouldUseAggregatedPerformance) {
    ignore.push('retirementAccount');
  }

  // if sort is productType, introduce productType group and remove retirement group; else introduce isOwner group
  if (sortFilterType && sortFilterType === 'productType') {
    ignore.push('retirementAccount');
    ignore = remove('productType', ignore);
  } else if (sortFilterType && sortFilterType === 'ownership') {
    ignore.push('retirementAccount');
    ignore.push('productType');
    ignore = remove('isOwner', ignore);
  }

  // 529-only clients should only see the PI bar (formatted to 529 College Savings) and no other groups
  if (is529Only) {
    ignore.push('retirementAccount');
  }

  return accountFilterType &&
    (accountFilterType.toLowerCase() === 'retirement' ||
      accountFilterType === 'nonRetirement' ||
      accountFilterType === 'Non-Retirement')
    ? columnDefsRetirementOrder.filter((curr) => !ignore.includes(curr.field))
    : columnDefs.filter((curr) => !ignore.includes(curr.field));
};

// agg funcs and associated

// priceChangePercentAggFunc/percentChangeValueGetter/createValueObject all used for Daily Price Change (%) aggregation
// see: https://www.ag-grid.com/react-grid/aggregation/#example-3---multi-column-aggregation
export const priceChangePercentAggFunc = (params) => {
  const {
    values,
    rowNode: { field, key }
  } = params;
  let balanceSum = 0;
  let previousBalanceSum = 0;

  //Special case for Pershing agreements, we do not want to aggregate the positions for the group
  if (field && field === 'groupId' && key) {
    const account = findMatchingAdviceGridAccount(key);
    if (account) {
      const { balance, previousBalanceValue } = account;
      balanceSum = balance ? balance : 0;
      previousBalanceSum = previousBalanceValue ? previousBalanceValue : 0;
      return createValueObject(balanceSum, previousBalanceSum);
    }
    return '';
  }

  values.forEach((value) => {
    balanceSum += value.balance;
    previousBalanceSum += value.previousBalanceValue;
  });

  return createValueObject(balanceSum, previousBalanceSum);
};

export const percentChangeValueGetter = ({ data = {} }) => {
  if (data.difference === EMPTY_CELL_DASH) {
    return createValueObject(data.balance, 0);
  }
  return createValueObject(data.balance, data.previousBalanceValue);
};

export const historicalBalancesAgg = (historicalBalances) => (params) => {
  if (
    params &&
    params.rowNode &&
    params.rowNode.field &&
    isRowGroup(params.rowNode.field) &&
    params.rowNode.field !== 'groupId'
  ) {
    if (params.values) {
      let sum = 0;
      params.values.forEach((value) => (sum += value));
      return sum;
    }
  }
  if (
    historicalBalances &&
    historicalBalances.balances &&
    historicalBalances.balances.length
  ) {
    if (params && params.rowNode && params.rowNode.key) {
      const balance = historicalBalances.balances.find(
        (currBalance) => currBalance.accountId === params.rowNode.key
      );
      if (balance) {
        return balance.endingMarketValue ? balance.endingMarketValue : 0;
      }
    }
  }
  return 0;
};

const createValueObject = (balance, previousBalance) => {
  return {
    balance: balance,
    previousBalanceValue: previousBalance,
    toString: () => {
      return percentChange(balance, previousBalance).toString();
    }
  };
};

// header templates

export const performanceHeaderTemplate = `<div class="ag-cell-label-container" role="presentation">
    <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
    <div ref="eLabel" class="ag-header-cell-label" role="presentation">
      <Fragment>
        <span class="ag-header-cell-text" role="columnheader">
          PRR <i class="aa-question-circle modal-info-icon"
            onclick={{event.stopPropagation()}}
            onmousedown={{document.body.querySelector('div.prr-disclosure').querySelector('a.modal-info-link').click()}}
            ontouchstart={{event.stopPropagation()}}
            ontouchend={{document.body.querySelector('div.prr-disclosure').querySelector('a.modal-info-link').click()}}
          ></i>
        </span>
        <br />
        <sub><span>Since Inception</span></sub>
      </Fragment>
      <span ref="eSortOrder" class="ag-header-icon ag-sort-order" ></span>
      <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" ></span>
      <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" ></span>
      <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" ></span>
    </div>
  </div>`;

// non-component cell renderers

export const PercentWithIndicator = ({ value }) => {
  if (isObject(value)) {
    // we have to make an assumption we have value object (ag grid convention) if incoming value is not a string
    if (value.toString) {
      return (
        <span className="percent indicator">
          {percentFormatter({ value: value.toString() })}
          <ValueIndicator value={value.toString()} />
        </span>
      );
    }
    return '';
  }
  return (
    <span className="percent indicator">
      {percentFormatter({ value: value })}
      <ValueIndicator value={percentFormatter({ value: value })} />
    </span>
  );
};

export const DollarWithIndicator = ({ value, data, node }) => {
  if (isObject(value)) {
    // we have to make an assumption we have value object (ag grid convention) if incoming value is not a string
    if (value.toString) {
      return (
        <span className="dollar indicator">
          {moneyFormatter({ value: value.toString(), data, node })}
          <ValueIndicator value={value.toString()} />
        </span>
      );
    }
    return '';
  }
  return (
    <span className="dollar indicator">
      {moneyFormatter({ value: value, data, node })}
      <ValueIndicator value={moneyFormatter({ value: value, data, node })} />
    </span>
  );
};

export const PriceWithIndicator = ({ value, data }) => {
  let format = '$0,0.00';

  if (data) {
    const { isMoneyMarket } = data;

    if (isMoneyMarket) {
      format = '$0,0.0000';
    }
    if (value === EMPTY_CELL_DASH || value === BLANK_CELL_PLACEHOLDER) {
      return value;
    }

    if (value) {
      return (
        <span className="dollar indicator">
          {formatDollarAmount(value, format)}
          <ValueIndicator value={formatDollarAmount(value, format)} />
        </span>
      );
    }
    return (
      <span className="dollar indicator">
        {formatDollarAmount(0, format)}
        <ValueIndicator value={formatDollarAmount(0, format)} />
      </span>
    );
  }
  return '';
};

export const AccountNameFormatter = ({
  valueFormatted,
  data,
  node,
  isCWV,
  uberId,
  historicalBalancesDate,
  isMobile,
  isPerformancePage,
  showIntradayRefresh
}) => {
  const { key, field } = node;
  if (!data && valueFormatted) {
    return <div className="group-label">{valueFormatted}</div>;
  } else if (data || field === 'groupId') {
    const account = data ? data : findMatchingAdviceGridAccount(key);

    if (!data && !account) {
      return null;
    }

    const {
      accountName,
      accountId,
      balanceAsOfDate,
      balanceValue,
      productType,
      accountDescriptor,
      accountNumberWChecksum,
      accountNumber,
      isViewOnly,
      sweepAccountId,
      workplaceRetirement,
      hideButtons,
      isPosition = false,
      closedAccount,
      disableAccountLink,
      disableAsOf,
      mmrMessage,
      ticker,
      cusip
    } = account;
    const showRefreshButton =
      isBrokerage(productType) &&
      isNil(historicalBalancesDate) &&
      showIntradayRefresh &&
      intradayEligibleFilter({ productType, balance: balanceValue }) &&
      !isPerformancePage;
    const renderLinks = () => {
      if (isActivePlus(productType) || isActiveAdvisory(productType)) {
        return (
          <a
            className="multiline-wrap account-link"
            href={`/personal-investing/tools/fund-research/${ticker}`}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(accountDescriptor)
            }}
          />
        );
      }

      if (cusip && !isViewOnly) {
        return (
          <Link
            className="multiline-wrap account-link"
            to={`/account-details?id=${accountId}&investment=${cusip}`}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(accountDescriptor)
            }}
          />
        );
      }
      return (
        <span
          className={'multiline-wrap'}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(accountDescriptor)
          }}
        />
      );
    };
    if (isPosition) {
      return (
        <div className="pinned-cell-align">
          {
            // link to MFRT if position is under an APP or RAS account
            renderLinks()
          }
        </div>
      );
    }

    const accountDetailsLink = (
      <Link
        className="multiline-wrap account-link"
        to={`/account-details?id=${accountId}`}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(accountDescriptor)
        }}
      />
    );
    const activePlusLink = (
      <a
        className="multiline-wrap account-link"
        href={activePlusAccountUrl(accountId, isCWV, uberId)}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(accountDescriptor)
        }}
      />
    );
    const brokerageLink = (
      <BrokerageLink
        accountId={accountId}
        accountName={accountDescriptor}
        className="account-link multiline-wrap"
      />
    );
    const noLink = (
      <span
        className="multiline-wrap no-link"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(accountDescriptor)
        }}
      />
    );

    const isAdvisoryProduct = isActiveAdvisory(productType);

    let display = valueFormatted;
    if (!valueFormatted) {
      if (
        isViewOnly ||
        isAdvisoryProduct ||
        isWorkplaceRetirement(productType) ||
        disableAccountLink
      ) {
        display = noLink;
      } else if (isBrokerage(productType)) {
        display = brokerageLink;
      } else if (isActivePlus(productType)) {
        display = activePlusLink;
      } else {
        display = accountDetailsLink;
      }
    }

    const benchmarkModelNumber = appBenchmarkTooltipStringToNumber(accountName);

    // Stops propagation of ag-grid context menu and cell selection for transaction buttons/menu
    const buttonRef = (buttonNode) => {
      if (buttonNode) {
        buttonNode.oncontextmenu = (e) => {
          e.stopPropagation();
        };

        buttonNode.onmousedown = (e) => {
          e.stopPropagation();
        };
      }
    };
    const brokerageAccountNum = accountNumberWChecksum
      ? accountNumberWChecksum.replace('-', '')
      : accountNumber
      ? accountNumber.replace('-', '')
      : '';
    return (
      <div className="pinned-cell-align cell-with-data">
        {accountDescriptor === 'Model Benchmark' ? (
          <div className="benchmark-container">
            {display}
            <BenchmarkModal
              flex={true}
              productType={productType}
              modelNumber={benchmarkModelNumber}
            />
          </div>
        ) : (
          <div className="multiline-wrap">
            {display}
            {closedAccount && <span className="tag-label"> (Closed)</span>}
            {!disableAsOf && !historicalBalancesDate && (
              <div className="account-as-of-date">
                <IntradayAsOfDate
                  productType={productType}
                  accountId={accountId}
                  asOfDate={balanceAsOfDate}
                  isPerformancePage={isPerformancePage}
                />
              </div>
            )}
            <span className="account-number-label blocked_data">
              {accountNumberWChecksum || accountNumber}
            </span>
            <AccountLabel accountId={accountId} />
            {showRefreshButton && (
              <Fragment>
                <br />
                <IntradayRefresh
                  containerClassName="intraday-refreshing-label"
                  hideIcon
                />
              </Fragment>
            )}
            {mmrMessage && (
              <div
                dangerouslySetInnerHTML={{
                  __html: `${mmrMessage} For questions, please call T. Rowe Price at 877-218-5162.`
                }}
              />
            )}
          </div>
        )}

        {showRefreshButton && <IntradayRefresh hideLabel />}
        {!isViewOnly && !workplaceRetirement && !hideButtons && (
          <div className="cell-buttons" ref={buttonRef}>
            <MoreButton
              data={data}
              accountId={accountId}
              accountNumber={brokerageAccountNum.replace('-', '')}
              sweepAccountId={sweepAccountId}
              productType={productType}
              isMobile={isMobile}
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

const mapStateToProps = (state) => ({
  uberId: getUberId(state),
  isCWV: getCustomerWebView(state),
  historicalBalancesDate: state.historicalBalances.selectedDate,
  showIntradayRefresh: getIntradayRefreshEnabled(state),
  isMobile: state.view.isMobileLayout,
  isPerformancePage: onPerformancePage(state)
});

export const AccountNameRenderer =
  connect(mapStateToProps)(AccountNameFormatter);

export const balanceValueAggr = (params) => {
  const {
    values,
    rowNode: { field, key }
  } = params;

  //Special case for Pershing agreements, we do not want to aggregate the positions for the group
  if (field && field === 'groupId' && key) {
    const {
      rowNode: { key }
    } = params;
    const account = findMatchingAdviceGridAccount(key);
    if (account) {
      const { balance } = account;
      return balance ? balance : 0;
    }
  }

  if (values) {
    let sum = 0;
    values.forEach((value) => (sum += value));
    return sum;
  }
  return 0;
};

// cell formatters

export const emptyFormatter = () => '';

export const dashFormatter = ({ node }) => (node.group ? '' : EMPTY_CELL_DASH);

export const moneyFormatter = ({ value }) =>
  value
    ? value === EMPTY_CELL_DASH || value === BLANK_CELL_PLACEHOLDER
      ? value
      : formatDollarAmount(value)
    : '$0.00';

export const priceChangeDollarAgg = (params) => {
  const {
    values,
    rowNode: { field, key }
  } = params;
  if (field && isRowGroup(field) && field !== 'groupId') {
    if (values) {
      let sum = 0;
      values.forEach((value) => (sum += value));
      return sum;
    }
  }
  if (key) {
    const {
      rowNode: { key }
    } = params;
    const account = findMatchingAdviceGridAccount(key);
    if (account) {
      const { difference } = account;
      return difference ? difference : 0;
    }
  }
  return 0;
};

export const priceFormatter = ({ value, data, node }) => {
  if (node.group) {
    return '';
  }
  const { accountNameAndNumber = '', isMoneyMarket = false } = data ? data : {};
  const isPershingAccount =
    accountNameAndNumber
      .toUpperCase()
      .includes('RETIREMENT ADVISORY SERVICE') ||
    accountNameAndNumber.toUpperCase().includes('ACTIVEPLUS PORTFOLIO') ||
    accountNameAndNumber.toUpperCase().includes('BROKERAGE') ||
    accountNameAndNumber.toUpperCase().includes('NULL');
  if (isPershingAccount) {
    return '';
  }
  const format = isMoneyMarket ? '$0,0.0000' : '$0,0.00';
  if (value === EMPTY_CELL_DASH || value === BLANK_CELL_PLACEHOLDER) {
    return value;
  }
  if (value) {
    return formatDollarAmount(value, format);
  }
  return formatDollarAmount(0, format);
};

export const percentFormatter = ({ value }) => {
  return value
    ? value === EMPTY_CELL_DASH
      ? value
      : formatPercentAmount(value)
    : '0.00%';
};

export const prrPercentFormatter =
  (hasMultipleAPP) =>
  ({ value, node }) => {
    if (node.group && Object.values(node.groupData)[0] !== 'activePlus') {
      return '';
    }

    if (
      node.group &&
      Object.values(node.groupData)[0] === 'activePlus' &&
      hasMultipleAPP
    ) {
      return '';
    }

    return !isNil(value)
      ? value === EMPTY_CELL_DASH
        ? value
        : formatPercentAmount(adjustPercent(value))
      : EMPTY_CELL_DASH;
  };

export const quantityFormatter = ({ value, data, node }) => {
  if (node.group) {
    return '';
  }
  const {
    accountNameAndNumber = '',
    isActivePlusOrBrokerage = false,
    productType
  } = data ? data : {};
  const isPershingAccount =
    accountNameAndNumber
      .toUpperCase()
      .includes('RETIREMENT ADVISORY SERVICE') ||
    accountNameAndNumber.toUpperCase().includes('ACTIVEPLUS PORTFOLIO') ||
    accountNameAndNumber.toUpperCase().includes('BROKERAGE') ||
    accountNameAndNumber.toUpperCase().includes('NULL');
  if (isPershingAccount) {
    return '';
  }

  if (value === EMPTY_CELL_DASH || value === BLANK_CELL_PLACEHOLDER) {
    return value;
  }
  let format = '0,0.000';
  if (isActivePlusOrBrokerage || isPershingProduct(productType)) {
    format = '0,0.0000';
  }
  if (value) {
    return numeral(value).format(format);
  }
  return numeral(0).format(format);
};

export const primaryOwnerFormatter = ({ value }) => {
  const line = value.split('\n');
  return (
    <Fragment>
      <p className="account-type-label">{line[0]}</p>
      <p className="account-owner-label blocked_data">{line[1]}</p>
    </Fragment>
  );
};

export const personalInvestmentFormatter = () => {
  return 'Personal Investments';
};

export const collegeSavingsPlanFormatter = () => {
  return '529 College Savings Plan';
};

export const alaska529PlanFormatter = () => {
  return '529 Plans';
};

export const workplaceRetirementFormatter = () => {
  return 'Workplace Retirement Accounts';
};

export const SmallBusinessRetirementTracFormatter = () => {
  return 'Small Business Retirement Plan Accounts';
};

const ProductClassDisclosure = ({ value }) => {
  const product =
    value === ALASKA_529_CLASS ||
    value === MARYLAND_529_CLASS ||
    value === NATIONAL_529_CLASS
      ? COLLEGE_SAVINGS_CLASS
      : value;
  const disclosure = (
    <DisclosureModal content={Content.rasPRRDisclosure} hideLinkContent />
  );

  return (
    <div className="product-class-row">
      {value === ALASKA_529_CLASS
        ? alaska529PlanFormatter()
        : products.types[product]
        ? products.types[product].label
        : ''}
      {isAdviceProductClass(product) && disclosure}
    </div>
  );
};

export const productClassFormatter = (value) => (
  <ProductClassDisclosure value={value} />
);

export const modelBenchmarkFormatter = ({ value }) => {
  return (
    <div className="model-benchmark multiline-wrap">
      Model Benchmark
      <BenchmarkModal
        productType={ACTIVE_ADVISORY}
        modelNumber={rasBenchmarkStringToNumber[value]}
      />
    </div>
  );
};

export const productTypeFormatter = ({ value }) => {
  return products.byAccount[value] ? products.byAccount[value].label : '';
};

export const retirementAccountFormatter = ({ value }) => {
  return value === 'true' ? 'Retirement' : 'Non-Retirement';
};

export const managementTypeFormatter = ({ value }) => {
  return value === 'true' ? 'Advisory' : 'Self-Managed';
};

export const ownerTypeFormatter = ({ value }) => {
  return value ? value : '';
};

export const dateFormatter = ({ value, node }) => {
  if (node.group) {
    return '';
  }
  return value ? mmddyyyy(value, '/') : EMPTY_CELL_DASH;
};

export const accountNameFormatter = ({ data }) => {
  if (data) {
    return data.isPosition
      ? data.accountName
      : `${data.accountDescriptor} ${
          data.accountNumberWChecksum || data.accountNumber
        }`;
  }

  return '';
};

// misc. components and functions

export const isAccountNameAndNumberField = (field) =>
  field === 'accountNameAndNumber' || field === undefined;

export const getContextMenuItems = ({ node, column }) => {
  const { data, key, field } = node || {};

  if (
    (field !== 'groupId' && isRowGroup(field)) ||
    (column && column.colId !== 'ag-Grid-AutoColumn')
  ) {
    return ['copy', 'copyWithHeaders'];
  }

  const isCustomerWebView = getCustomerWebView(Store.getState());
  const account = findMatchingAdviceGridAccount(key);
  const { accountId, productType, isViewOnly, disableAccountLink } = data
    ? data
    : account
    ? account
    : {};
  const disableLink = shouldDisableContextMenuLink(
    isCustomerWebView,
    accountId,
    isViewOnly,
    productType,
    disableAccountLink,
    column
  );

  return [
    'copy',
    'copyWithHeaders',
    {
      name: 'Open link in new tab',
      disabled: disableLink,
      action: () => {
        if (isBrokerage(productType)) {
          openBrokerageAuthTab(accountId);
        } else if (isActivePlus(productType)) {
          // This link does not work in CWV.  Couldn't figure out how to pass the
          // state through to this function.
          open(activePlusAccountUrl(accountId));
        } else {
          open(
            `${GLOBALS.AAM_UI_TRP_APP_CONTEXT}/account-details?id=${accountId}`
          );
        }
      }
    }
  ];
};

export const shouldDisableContextMenuLink = (
  isCustomerWebView,
  accountId,
  isViewOnly,
  productType,
  disableAccountLink,
  column
) => {
  const hasData =
    !isNil(accountId) && !isNil(isViewOnly) && !isNil(productType);

  if (isCustomerWebView && isBrokerage(productType)) {
    return true;
  }

  return (
    disableAccountLink ||
    !hasData ||
    isNil(column) ||
    isViewOnly ||
    isActiveAdvisory(productType) ||
    isWorkplaceRetirement(productType)
  );
};

// Applies value formatters to cell data when copying; not doing so copies raw data
export const processCellForClipboard = (params) => {
  const { column, node, value } = params;
  const colDef = column.getColDef();
  const valueFormatterParams = {
    ...params,
    data: node?.data,
    colDef
  };

  if (colDef.valueFormatter) {
    return colDef.valueFormatter(valueFormatterParams);
  } else if (node?.field === 'retirementAccount') {
    return retirementAccountFormatter(valueFormatterParams);
  } else if (node?.field === 'productType') {
    return productTypeFormatter(valueFormatterParams);
  } else if (node?.field === 'productClass') {
    // Does not use productClassFormatter due to returning Object rather than simple label
    return products.types[value] ? products.types[value].label : value;
  } else if (
    node?.field === 'accountName' &&
    column.colId === 'ag-Grid-AutoColumn'
  ) {
    const nameAndNumber = node?.key.split(';');
    return `${nameAndNumber[0]} ${nameAndNumber[2]}`;
  } else if (isAccountNameAndNumberField(node?.field)) {
    return accountNameFormatter(valueFormatterParams);
  } else if (isArray(value)) {
    const moneyParams = { ...params, value: value[0] };
    return moneyFormatter(moneyParams);
  } else if (node?.field === 'groupId') {
    const { key } = node;
    if (key) {
      const account = findMatchingAdviceGridAccount(key);
      if (
        (account &&
          account.accountDescriptor &&
          account.accountNumberWChecksum) ||
        account.accountNumber
      ) {
        return `${account.accountDescriptor} ${
          account.accountNumberWChecksum || account.accountNumber
        }`;
      }
    }
  }

  return value;
};

export const formatterMapper = (field, key) => {
  if (field === 'personalInvestment') {
    return personalInvestmentFormatter(key);
  } else if (field === 'managementType') {
    return managementTypeFormatter(key);
  } else if (field === 'retirementAccount') {
    return retirementAccountFormatter(key);
  } else if (field === 'productType') {
    return productTypeFormatter(key);
  }

  return '';
};

export const GridExportToCSV = ({ buttonClass, onClick }) => (
  <button className={buttonClass} onClick={() => onClick()}>
    <ExportToCSV />
    <span>Download</span>
  </button>
);

export const ViewAllBar = ({ expanded, setExpanded, children }) => {
  return (
    <div className="pure-u-1 show-more-accounts-bar">
      <button
        onClick={() => setExpanded()}
        className="show-more-accounts-button">
        <Chevron
          className={expanded ? 'chevron-icon' : 'chevron-icon-inverted'}
        />
        {children}
      </button>
    </div>
  );
};

export const findMatchingAdviceGridAccount = (accountId) => {
  const accounts = getAdviceGridAccounts(Store.getState());
  const account = accounts.find((acct) => acct.accountId === accountId);

  return account ? account : null;
};

export const getMultipleAPPStatus = (filteredAPPAccounts, accounts, closed) => {
  // exclude model benchmark
  if (
    filteredAPPAccounts.length === 2 &&
    filteredAPPAccounts[0].accountId === filteredAPPAccounts[1].accountId
  ) {
    return false;
  }
  // When one closed APP account exists , check for open APP accounts
  if (filteredAPPAccounts.length === 1 && closed) {
    return (
      accounts.filter((account) => isActivePlus(account.productType)).length > 1
    );
  }
  if (filteredAPPAccounts.length === 1 && !closed) {
    return false;
  }
  if (filteredAPPAccounts.length > 2) {
    return true;
  }
};
